import React, { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { ReportService } from '../../services/reportService';
import {
  PerformanceTrendReportData,
  OperatorPerformanceSummary,
  OperatorDrilldownResult,
  CategoryTrendItem,
} from '../../types/PerformanceTrendReport';
import { ReportExportButton } from './ReportExportButton';

export const PerformanceTrendReport: React.FC = () => {
  const [report, setReport] = useState<PerformanceTrendReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [drilldown, setDrilldown] = useState<OperatorDrilldownResult | null>(null);
  const [drilldownLoading, setDrilldownLoading] = useState(false);

  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 90);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    setDrilldown(null);
    try {
      const data = await ReportService.getPerformanceTrendReport(startDate, endDate);
      setReport(data);
    } catch {
      setError('Failed to load performance trend report.');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleOperatorClick = async (op: OperatorPerformanceSummary) => {
    setDrilldownLoading(true);
    try {
      const data = await ReportService.getOperatorDrilldown(op.operatorId, startDate, endDate);
      setDrilldown(data);
    } catch {
      setError('Failed to load operator drilldown.');
    } finally {
      setDrilldownLoading(false);
    }
  };

  const getTrendIcon = (trend: number | null) => {
    if (trend === null) return <ArrowForwardIcon fontSize="small" sx={{ color: 'text.secondary' }} />;
    if (trend > 0) return <ArrowUpwardIcon fontSize="small" sx={{ color: 'success.main' }} />;
    if (trend < 0) return <ArrowDownwardIcon fontSize="small" sx={{ color: 'error.main' }} />;
    return <ArrowForwardIcon fontSize="small" sx={{ color: 'text.secondary' }} />;
  };

  const getCoachingChip = (flag: string | null) => {
    if (flag === 'Urgent') return <Chip label="Urgent" color="error" size="small" />;
    if (flag === 'Warning') return <Chip label="Warning" color="warning" size="small" />;
    return <Chip label="OK" color="success" size="small" variant="outlined" />;
  };

  // Rendered above every branch — loading, error and empty included. A period that fails to load
  // or returns nothing is exactly when a user needs to change the period, and returning a bare
  // Alert leaves them with no control but to leave the tab and come back.
  const dateFilter = (
    <Paper sx={{ p: 2, mb: 3 }}>
      <Grid container spacing={2} alignItems="center">
        <Grid>
          <TextField
            label="Start Date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
        </Grid>
        <Grid>
          <TextField
            label="End Date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
        </Grid>
        <Grid>
          <Button variant="contained" onClick={fetchReport} size="small">
            Apply
          </Button>
        </Grid>
        <Grid>
          <ReportExportButton
            report="performance-trend"
            label="Performance Trending"
            startDate={startDate}
            endDate={endDate}
            busy={loading}
          />
        </Grid>
        <Grid size={12}>
          <Typography variant="caption" color="text.secondary">
            Dates are calendar days in the agency&apos;s timezone.
          </Typography>
        </Grid>
      </Grid>
    </Paper>
  );

  if (loading) {
    return (
      <Box>
        {dateFilter}
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress aria-label="Loading performance trend report" />
        </Box>
      </Box>
    );
  }

  if (error && !report) {
    return (
      <Box>
        {dateFilter}
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!report || report.totalOperators === 0) {
    return (
      <Box>
        {dateFilter}
        <Alert severity="info">
          No completed calls with operator data found for the selected date range. An export of this
          period will contain the report headings and no data rows.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {dateFilter}

      {/* Summary cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4">{report.totalOperators}</Typography>
            <Typography variant="body2" color="text.secondary">Operators</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4">{report.totalCalls}</Typography>
            <Typography variant="body2" color="text.secondary">Total Calls</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4">{report.overallAverageScore}</Typography>
            <Typography variant="body2" color="text.secondary">Avg Score</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Drilldown back button */}
      {drilldown && (
        <Box sx={{ mb: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => setDrilldown(null)}
            variant="outlined"
            size="small"
          >
            Back to all operators
          </Button>
        </Box>
      )}

      {/* Drilldown view */}
      {drilldown ? (
        <DrilldownView drilldown={drilldown} loading={drilldownLoading} />
      ) : (
        /* Operator table */
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Operator</TableCell>
                <TableCell align="right">Calls</TableCell>
                <TableCell align="right">Avg Score</TableCell>
                <TableCell align="right">Trend (30d)</TableCell>
                <TableCell align="center">Coaching</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {report.operators.map((op) => (
                <TableRow
                  key={op.operatorId}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleOperatorClick(op)}
                >
                  <TableCell>{op.operatorName}</TableCell>
                  <TableCell align="right">{op.totalCalls}</TableCell>
                  <TableCell align="right">{op.averageScore}</TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                      {getTrendIcon(op.trend30d)}
                      {op.trend30d !== null ? `${op.trend30d > 0 ? '+' : ''}${op.trend30d}` : '—'}
                    </Box>
                  </TableCell>
                  <TableCell align="center">{getCoachingChip(op.coachingFlag)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {drilldownLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      )}
    </Box>
  );
};

interface DrilldownViewProps {
  drilldown: OperatorDrilldownResult;
  loading: boolean;
}

const DrilldownView: React.FC<DrilldownViewProps> = ({ drilldown, loading }) => {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (drilldown.categoryTrends.length === 0) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        No category score data available for this operator.
      </Alert>
    );
  }

  // Collect all unique periods across categories
  const allPeriods = Array.from(
    new Set(drilldown.categoryTrends.flatMap((c) => c.periods.map((p) => p.period)))
  ).sort();

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6">{drilldown.operatorName}</Typography>
        <Typography variant="body2" color="text.secondary">
          {drilldown.totalCalls} calls — Avg Score: {drilldown.averageScore}
        </Typography>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Category</TableCell>
              {allPeriods.map((p) => (
                <TableCell key={p} align="right">{p}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {drilldown.categoryTrends.map((cat: CategoryTrendItem) => {
              const scoreMap = new Map(cat.periods.map((p) => [p.period, p]));
              return (
                <TableRow key={cat.categoryName}>
                  <TableCell>{cat.categoryName}</TableCell>
                  {allPeriods.map((period) => {
                    const score = scoreMap.get(period);
                    return (
                      <TableCell key={period} align="right">
                        {score ? `${score.averageScore} (${score.callCount})` : '—'}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
